/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import {
  findCaptable as findCaptableService,
  getCaptablebyCompany as getCaptablebyCompanyService,
  getWaterfallBreakpoint as getWaterfallBreakpointService,
  toggleWaterfallVisibility as toggleWaterfallVisibilityService,
} from 'dashboard409a/services/captable';
import {
  clearDashboardMeasurementDate,
  selectDashboardCompany,
  selectDashboardMeasurementDate,
} from 'dashboard409a/states/features/global';
import { ILoadWaterfallRequest, ILoadWaterfallResponse, IWaterfallState } from './types';

const initialState: IWaterfallState = {
  loading: false,
  waterfallBreakpoint: null,
  currentCaptable: null,
  isWaterfallVisible: true,
};

export const loadWaterfall = createAsyncThunk<ILoadWaterfallResponse, ILoadWaterfallRequest>(
  'waterfall/loadWaterfall',
  async ({ companyId, measurementDateId }, thunkAPI) => {
    try {
      const captableList = await getCaptablebyCompanyService(companyId, { mp: measurementDateId });
      const primaryCaptableId = captableList.find(captable => captable.is_primary)?.id;

      if (!primaryCaptableId) {
        return {
          waterfallBreakpoint: null,
          currentCaptable: null,
        };
      }

      const [waterfallBreakpoint, currentCaptable] = await Promise.all([
        getWaterfallBreakpointService(primaryCaptableId),
        findCaptableService(primaryCaptableId),
      ]);

      return {
        waterfallBreakpoint,
        currentCaptable,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const toggleWaterfallVisibility = createAsyncThunk<
  { captableId: number; visible: boolean },
  { captableId: number; visible: boolean }
>('waterfall/toggleVisibility', async ({ captableId, visible }, thunkAPI) => {
  try {
    await toggleWaterfallVisibilityService(captableId, visible);
    enqueueSnackbar('Waterfall visibility toggled', { variant: 'success' });
    return { captableId, visible };
  } catch (error) {
    return thunkAPI.rejectWithValue(error);
  }
});

const watefallSlice = createSlice({
  name: 'waterfall',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(selectDashboardCompany, () => initialState);
    builder.addCase(selectDashboardMeasurementDate, () => initialState);
    builder.addCase(clearDashboardMeasurementDate, () => initialState);
    builder.addCase(loadWaterfall.pending, (state, action) => {
      state.loading = true;
      state.waterfallBreakpoint = null;
      state.currentCaptable = null;
      state.loadRequestId = action.meta.requestId;
    });
    builder.addCase(loadWaterfall.fulfilled, (state, action) => {
      if (state.loadRequestId !== action.meta.requestId) {
        return;
      }

      state.loading = false;
      state.loadRequestId = undefined;
      state.waterfallBreakpoint = action.payload.waterfallBreakpoint;
      state.currentCaptable = action.payload.currentCaptable;

      if (action.payload.waterfallBreakpoint?.is_waterfall_visible !== undefined) {
        state.isWaterfallVisible = action.payload.waterfallBreakpoint.is_waterfall_visible;
      }
    });
    builder.addCase(loadWaterfall.rejected, (state, action) => {
      if (state.loadRequestId !== action.meta.requestId) {
        return;
      }

      state.loading = false;
      state.loadRequestId = undefined;
    });
    builder.addCase(toggleWaterfallVisibility.fulfilled, (state, action) => {
      if (state.currentCaptable?.id === action.payload.captableId) {
        state.isWaterfallVisible = action.payload.visible;
      }
    });
  },
});

export default watefallSlice.reducer;
